/**
 * Matchday time helpers — countdown segments, kickoff labels, and the
 * "MM-DD" key used by the club feed's this-day archive. All pure.
 */
import type { Difficulty, Fixture, H2HRecord } from './types';

export interface Countdown {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalMs: number;
}

export function countdownTo(kickoffUtc: string | null, nowMs: number = Date.now()): Countdown {
  const totalMs = Math.max(0, Date.parse(kickoffUtc ?? '') - nowMs);
  const s = Math.floor(totalMs / 1000);
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
    totalMs,
  };
}

/** "Sat 16 Aug · 15:00" in the viewer's local time. */
export function kickoffLabel(kickoffUtc: string | null): string {
  const t = Date.parse(kickoffUtc ?? '');
  if (!Number.isFinite(t)) return '';
  const d = new Date(t);
  const day = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return `${day} · ${time}`;
}

function literalCalendarDate(value?: string): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : null;
}

/** "Sat 16 Aug" without implying a known kick-off time. */
export function dateLabel(kickoffUtc: string | null, kickoffDate?: string): string {
  if (kickoffUtc) {
    const t = Date.parse(kickoffUtc); if (!Number.isFinite(t)) return '';
    return new Date(t).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  }
  const date = literalCalendarDate(kickoffDate);
  return date?.toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' }) ?? '';
}

export function fixtureTimeLabel(fixture: Pick<Fixture, 'kickoffUtc' | 'kickoffDate' | 'timeTbc'>): string {
  return fixture.timeTbc ? `${dateLabel(fixture.kickoffUtc, fixture.kickoffDate)} · kick-off TBC` : kickoffLabel(fixture.kickoffUtc);
}

export function confirmedKickoffIsFuture(fixture: Pick<Fixture, 'kickoffUtc'> & Pick<Partial<Fixture>, 'statusQuality'>, now = Date.now()): boolean {
  return fixture.statusQuality !== 'overdue' && fixture.kickoffUtc != null && Number.isFinite(Date.parse(fixture.kickoffUtc)) && Date.parse(fixture.kickoffUtc) > now;
}

function londonCalendarDate(now: number): string {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(now)).map((part) => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** A TBC fixture remains upcoming until its London calendar day has ended. */
export function fixtureIsUpcoming(fixture: Pick<Fixture, 'kickoffUtc' | 'kickoffDate'> & Pick<Partial<Fixture>, 'statusQuality'>, now = Date.now()): boolean {
  if (fixture.statusQuality === 'overdue') return false;
  if (fixture.kickoffUtc) return Number.isFinite(Date.parse(fixture.kickoffUtc)) && Date.parse(fixture.kickoffUtc) > now;
  return Boolean(fixture.kickoffDate && /^\d{4}-\d{2}-\d{2}$/.test(fixture.kickoffDate) && fixture.kickoffDate >= londonCalendarDate(now));
}

/** Stable ordering only; TBC dates deliberately imply no kick-off time. */
export function fixtureOrderTime(fixture: Pick<Fixture, 'kickoffUtc' | 'kickoffDate'>): number {
  return Date.parse(fixture.kickoffUtc ?? `${fixture.kickoffDate ?? ''}T12:00:00.000Z`);
}

/** Short month bucket for the fixtures month nav ("Aug", "Sep", …). */
export function monthOf(kickoffUtc: string | null, kickoffDate?: string): string {
  if (kickoffUtc) { const t = Date.parse(kickoffUtc); return Number.isFinite(t) ? new Date(t).toLocaleDateString('en-GB', { month: 'short' }) : ''; }
  return literalCalendarDate(kickoffDate)?.toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'short' }) ?? '';
}

/** "MM-DD" key for the this-day archive, from the viewer's local date. */
export function thisDayKey(now: Date = new Date()): string {
  return `${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function fixtureDateParts(f: Fixture): { day: string; mon: string } {
  const timed = f.kickoffUtc ? new Date(f.kickoffUtc) : null;
  const d = timed && Number.isFinite(timed.getTime()) ? timed : literalCalendarDate(f.kickoffDate);
  if (!d) return { day: '', mon: '' };
  const stable = f.kickoffUtc ? {} : { timeZone: 'UTC' as const };
  return {
    day: d.toLocaleDateString('en-GB', { ...stable, day: 'numeric' }),
    mon: d.toLocaleDateString('en-GB', { ...stable, month: 'short' }),
  };
}

export const COMP_SHORT: Record<string, string> = {
  'Premier League': 'PL',
  'Champions League': 'UCL',
  'Community Shield': 'Shield',
  'FA Cup': 'FA Cup',
  'EFL Cup': 'EFL Cup',
  'Friendly': 'PSF',
};

export function compShort(comp: string): string {
  return COMP_SHORT[comp] ?? comp;
}

export type CompKind = 'league' | 'ucl' | 'cup' | 'shield' | 'friendly' | 'other';
export interface CompBadge { short: string; kind: CompKind; available: boolean; }

const COMP_KIND: Record<string, CompKind> = {
  'Premier League': 'league',
  'Champions League': 'ucl',
  'Community Shield': 'shield',
  'FA Cup': 'cup',
  'EFL Cup': 'cup',
  'Friendly': 'friendly',
};

export function competitionBadge(comp: string): CompBadge {
  const kind = COMP_KIND[comp] ?? 'other';
  return { short: compShort(comp), kind, available: kind !== 'ucl' };
}

export function difficultyFromH2H(h2h?: H2HRecord): Difficulty | null {
  const r = h2h?.record;
  if (!r) return null;
  const n = r.w + r.d + r.l;
  if (n === 0) return null;
  const winRate = r.w / n;
  if (winRate >= 0.55) return 'winnable';
  if (winRate <= 0.30) return 'hard';
  return 'mid';
}

/**
 * Returns the short day-of-week ("Mon"…"Sun") for the given ISO kickoff
 * string in the viewer's local time. Returns '' if the date is invalid.
 */
export function dayOfWeek(iso: string | null, date?: string): string {
  if (iso) { const t = Date.parse(iso); return Number.isFinite(t) ? new Date(t).toLocaleDateString('en-GB', { weekday: 'short' }) : ''; }
  return literalCalendarDate(date)?.toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'short' }) ?? '';
}
